import React from 'react';
import i18n from '../../../../../i18n';
import { sourceMeta } from '../../../data/reservationSources';
import { fmt } from '../../../data/metrics';
import type { ReservationSourceId, SourceBreakdown } from '../../../types';
import '../../../styles/drill-noshow.css';

/** The drivers' minimum reservations before a rate is read (drill_drivers.MIN_BUCKET_SAMPLE). */
export const RATE_MIN_SAMPLE = 8;

export type SourceRateRow = {
  source: ReservationSourceId;
  label: string;
  color: string;
  /** The channel's rate: its events over its own base. */
  rate: number;
  /** The channel's share of every event in scope (0–100). */
  sharePct: number;
  /** The channel's base; null when the payload predates it. */
  n: number | null;
  muted: boolean;
};

export function isRateMixMetric(
  metricKey: string,
): metricKey is 'noshow' | 'cancel' {
  return metricKey === 'noshow' || metricKey === 'cancel';
}

/**
 * Per-channel no-show / cancellation rate with the channel's share of the
 * events as the secondary number. Channels below the driver minimum sort
 * last and are drawn pale.
 */
export function buildSourceRateRows(
  breakdown: SourceBreakdown | undefined,
  metricKey: 'noshow' | 'cancel',
): SourceRateRow[] {
  const sources = breakdown?.sources ?? [];
  const count = (s: (typeof sources)[number]) =>
    (metricKey === 'noshow' ? s.noShowCount : s.cancelCount) ?? 0;
  const total = sources.reduce((a, s) => a + count(s), 0);
  const rows = sources
    .map(s => {
      const base = metricKey === 'noshow' ? s.noshowBase : s.cancelBase;
      const n = base ?? null;
      const m = sourceMeta(s.source);
      return {
        source: s.source,
        label: m.label,
        color: m.color,
        rate: metricKey === 'noshow' ? s.noshow : s.cancel,
        sharePct: total ? +((count(s) / total) * 100).toFixed(1) : 0,
        n,
        muted: n != null && n < RATE_MIN_SAMPLE,
      };
    })
    .filter(r => (r.n == null ? r.rate > 0 || r.sharePct > 0 : r.n > 0));
  return rows.sort(
    (a, b) => Number(a.muted) - Number(b.muted) || b.rate - a.rate,
  );
}

export function DrillSourceRateList({
  breakdown,
  metricKey,
  limit,
}: {
  breakdown?: SourceBreakdown;
  metricKey: 'noshow' | 'cancel';
  limit?: number;
}) {
  const rows = buildSourceRateRows(breakdown, metricKey);
  if (!rows.length) return null;
  const shown = limit ? rows.slice(0, limit) : rows;
  const max = Math.max(...rows.filter(r => !r.muted).map(r => r.rate), 0.0001);
  const rowKey = metricKey === 'cancel' ? 'rowCancel' : 'rowNoshow';
  return (
    <div className="ddrill-ns-rows" data-testid="source-rate-list">
      {shown.map(r => (
        <div
          className={`ddrill-ns-row${r.muted ? ' is-muted' : ''}`}
          key={r.source}
          data-muted={r.muted ? 'true' : undefined}
        >
          <span className="dcrux-si-name">
            <span
              className="dcrux-comp-dot"
              style={{ background: r.color, marginRight: 6 }}
            />
            {r.label}
          </span>
          <span className="dcrux-si-track">
            <span
              className="dcrux-si-fill"
              style={{
                width: `${Math.max(2, Math.min(100, (r.rate / max) * 100))}%`,
                background: r.color,
              }}
            />
          </span>
          <span className="dcrux-si-val">{r.rate}%</span>
          <span className="ddrill-ns-sub">
            {i18n.t(`performance:noshowSubstance.source.${rowKey}`, {
              share: r.sharePct,
              n: r.n != null ? fmt.num(r.n) : '–',
            })}
          </span>
        </div>
      ))}
      {rows.some(r => r.muted) ? (
        <p className="ddrill-ns-note is-quiet">
          {i18n.t('performance:noshowSubstance.source.muted', {
            min: RATE_MIN_SAMPLE,
          })}
        </p>
      ) : null}
    </div>
  );
}
